import { captureException } from '@sentry/nextjs';
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { useMemo } from 'react';

import { v1Api } from '@/api/backendApi';
import { useIsLoggedIn } from '@/hooks/auth';
import { useSettings } from '@/hooks/state/useSettings';
import { userKeys } from '@/utils/queryKeys';

interface Markers {
  error: {
    code: number;
    message: string;
    details: string;
  } | null;
  favourites_data: number[];
  mg_downloads: number[];
  sfx_variant_downloads: number[];
  track_downloads: number[];
  video_downloads: number[];
}

// 10 seconds stale.
const REFETCH_INTERVAL = 10_000;

const getUserMetadata = () =>
  v1Api
    .get<Markers>('page/user_metadata', {
      headers: { 'X-Skip-CDN': 'true' },
    })
    .then((r) => r.data);

export const useUserMetadata = () => {
  const queryClient = useQueryClient();
  const { settings } = useSettings();
  const { asPath } = useRouter();

  const isLoggedIn = useIsLoggedIn();

  const {
    data: userMetadata,
    refetch,
    isLoading,
  } = useQuery<Markers>({
    queryKey: userKeys.metadata(String(settings?.user?.id ?? '')),
    queryFn: getUserMetadata,
    refetchOnMount: true,
    staleTime: REFETCH_INTERVAL,
    placeholderData: keepPreviousData,
    enabled: isLoggedIn,
  });

  if (userMetadata?.error) {
    captureException(userMetadata.error?.details);
  }

  // Memoize the return value to prevent unnecessary re-renders
  return useMemo(
    () => ({
      trackDownloaded: userMetadata?.track_downloads ?? [],
      sfxDownloaded: userMetadata?.sfx_variant_downloads ?? [],
      mgDownloaded: userMetadata?.mg_downloads ?? [],
      svDownloaded: userMetadata?.video_downloads ?? [],
      isLoading: isLoading && isLoggedIn,
    }),
    [
      userMetadata?.track_downloads,
      userMetadata?.sfx_variant_downloads,
      userMetadata?.mg_downloads,
      isLoading,
      isLoggedIn,
      userMetadata?.video_downloads,
    ]
  );
};
